import React, { useCallback, useEffect, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Spinner } from 'equasion-lib';
import ApiError from '@/lib/shared-coms/ApiError';
import { useRootStore } from '@/lib/providers/RootProvider';
import QuarantineCleanup from './QuarantineCleanup';
import type { CleanupList } from './quarantineCleanupSteps';

// Zones hang off SHIPPING locations (Pick Path Setup asks the same question), and a cleanup — like
// its pick path — is one floor: the destination must be at the same location as what was picked.
export const QUARANTINE_CLEANUP_LOCATIONS = gql`
  query QuarantineCleanupLocations($companyId: ID!) {
    locations(companyId: $companyId, type: "shipping") {
      id
      companyName
      address {
        lineOne
        city
        state
      }
    }
  }
`;

// Plain strings rather than a fragment so scripts/gql-frontend-validate.cjs resolves them against
// the schema too.
const CLEANUP_ZONE_FIELDS = `
  id
  name
  zoneType
  pickSequence
`;

export const QUARANTINE_CLEANUP_LIST = gql`
  query quarantineCleanupList($companyId: ID!, $locationId: ID!) {
    quarantineCleanupList(companyId: $companyId, locationId: $locationId) {
      locationId
      pathConfigured
      totalQuantity
      notes
      stops {
        rank
        zone {
          ${CLEANUP_ZONE_FIELDS}
        }
        lines {
          zoneInventoryBalanceId
          productName
          ndc
          tenDigitNdc
          upc
          deaSchedule
          kind
          lot
          expiration
          quantity
          bulkQuantity
          units {
            serializedItemId
            serialNumber
            gtin
          }
          matchKeys
        }
      }
      eventBudget {
        maxEvents
        perBulkLine
        perUnit
      }
    }
  }
`;

export const QUARANTINE_CLEANUP_COMPLETE = gql`
  mutation quarantineCleanupComplete(
    $companyId: ID!
    $locationId: ID!
    $quarantineZoneId: ID!
    $picks: [QuarantineCleanupPickInput!]!
  ) {
    quarantineCleanupComplete(
      companyId: $companyId
      locationId: $locationId
      quarantineZoneId: $quarantineZoneId
      picks: $picks
    ) {
      quarantineZone {
        ${CLEANUP_ZONE_FIELDS}
      }
      movedQuantity
      lineCount
      zoneCount
      packagingCount
    }
  }
`;

export type CleanupResult = {
  quarantineZone: { id: string; name: string };
  movedQuantity: number;
  lineCount: number;
  zoneCount: number;
  packagingCount: number;
};

function QuarantineCleanupData() {
  const companyId = useRootStore((state) => state.selectedCompany.id) as string;
  const client = useApolloClient();
  const [locationId, setLocationId] = useState<string | null>(null);
  const [list, setList] = useState<CleanupList | null>(null);
  const [listError, setListError] = useState<unknown>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [completeMutation] = useMutation(QUARANTINE_CLEANUP_COMPLETE);

  const {
    data: locationsData,
    loading: locationsLoading,
    error: locationsError
  } = useQuery(QUARANTINE_CLEANUP_LOCATIONS, {
    fetchPolicy: 'network-only',
    variables: { companyId }
  });
  const locations = locationsData?.locations || [];

  // One floor means no question to ask.
  useEffect(() => {
    if (!locationId && locations.length === 1) setLocationId(locations[0].id);
  }, [locations, locationId]);

  // Newest-request-wins: only the answer to the LAST read is applied, so a slow read for a location
  // the picker has since left (or a reload overtaken by another) can never replace the list on
  // screen with someone else's zones.
  const requestSeqRef = useRef(0);

  // Read on demand (start, start another, try again) rather than kept live: the list is the
  // picker's walk, and must not reshuffle under them while they pick. Every read starts from no
  // list, so a stale one — the stock a finished cleanup just moved — is never offered to walk.
  const loadList = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setList(null);
    setListError(null);
    if (!locationId) {
      setLoadingList(false);
      return null;
    }
    setLoadingList(true);
    try {
      const { data } = await client.query({
        query: QUARANTINE_CLEANUP_LIST,
        variables: { companyId, locationId },
        fetchPolicy: 'network-only'
      });
      if (seq !== requestSeqRef.current) return null;
      setList(data?.quarantineCleanupList || null);
      return data?.quarantineCleanupList || null;
    } catch (e) {
      if (seq === requestSeqRef.current) setListError(e);
      return null;
    } finally {
      if (seq === requestSeqRef.current) setLoadingList(false);
    }
  }, [client, companyId, locationId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const complete = useCallback(
    async (
      quarantineZoneId: string,
      picks: {
        zoneInventoryBalanceId: string;
        bulkQuantity: number;
        serializedItemIds: string[];
      }[]
    ): Promise<CleanupResult> => {
      const { data } = await completeMutation({
        variables: { companyId, locationId, quarantineZoneId, picks }
      });
      return data?.quarantineCleanupComplete;
    },
    [companyId, completeMutation, locationId]
  );

  if (locationsLoading) return <Spinner />;
  if (locationsError) return <ApiError error={locationsError} />;

  return (
    <QuarantineCleanup
      companyId={companyId}
      locations={locations}
      locationId={locationId}
      setLocationId={setLocationId}
      list={list}
      loadingList={loadingList}
      listError={listError}
      onReload={loadList}
      onComplete={complete}
    />
  );
}

export default QuarantineCleanupData;
